import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtUSD } from "../../lib/api";
import { countdownFor, demandFor, daysLeft } from "../../lib/dealMeta";

const FILTERS = [["all", "All"], ["open", "Open"], ["soon", "Closing soon"], ["committed", "Committed"], ["closed", "Closed"]];

export default function Deals() {
  const [deals, setDeals] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [filter, setFilter] = useState(() => localStorage.getItem("solera_deal_filter") || "all");

  useEffect(() => {
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => setDeals([]));
    api.get("/commitments/mine").then((r) => setCommitments(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { localStorage.setItem("solera_deal_filter", filter); }, [filter]);

  if (!deals) return <span className="t-label">Loading</span>;

  const shown = deals.filter((d) => {
    if (filter === "open") return d.status === "open";
    if (filter === "closed") return d.status !== "open";
    if (filter === "committed") return !!d.my_commitment;
    if (filter === "soon") {
      const n = daysLeft(d.closes_date);
      return d.status === "open" && n != null && n >= 0 && n <= 7;
    }
    return true;
  });

  return (
    <div className="rise" data-testid="deals-page">
      <span className="t-label">Deals</span>
      <h1 className="t-h1 text-bone mt-3">Available investments</h1>
      <p className="t-small text-muted-s mt-2 measure">
        Each deal carries a memo with a fixed contents list. The last section, what we do not
        know, is never omitted.
      </p>

      <div className="flex gap-2 flex-wrap mt-8" data-testid="deal-filters">
        {FILTERS.map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`t-label px-3 py-1.5 border transition-colors duration-150 ${filter === key ? "border-[#E8E6E1] text-bone" : "border-[#24272B] text-muted-s hover:!text-[#E8E6E1]"}`}
            data-testid={`deal-filter-${key}`}>{label}</button>
        ))}
      </div>

      <div className="mt-8 space-y-0">
        {shown.map((d) => {
          const cd = countdownFor(d.status, d.closes_date, d.closes_at);
          const demand = demandFor(d.status, d.target_allocation_usd, d.minimum_usd, d.interest_count);
          return (
          <Link key={d.deal_id} to={`/app/deals/${d.deal_id}`}
            className="hairline py-6 flex flex-wrap gap-x-10 gap-y-3 items-baseline justify-between group block"
            data-testid={`deal-row-${d.deal_id}`}>
            <div className="min-w-[220px]">
              <span className="t-body text-bone group-hover:underline underline-offset-4">{d.company}</span>
              {d.circle && <span className="t-label !text-[#C69A4E] ml-2" data-testid={`deal-circle-${d.deal_id}`}>{d.circle.name}</span>}
              <div className="t-cap text-muted-s mt-1">{d.sector} · {d.region} · {d.stage}</div>
            </div>
            <div className="t-small text-muted-s min-w-[120px]">{d.round_label}</div>
            <div className="fig t-small text-bone min-w-[110px]">Min {fmtUSD(d.minimum_usd)}</div>
            <div className="min-w-[150px]">
              {d.status === "open" && d.interest_count > 0 && (
                <>
                  <span className="fig t-cap text-muted-s" data-testid={`deal-interest-${d.deal_id}`}>{d.interest_count} interested</span>
                  {demand && (
                    <div className="mt-1.5 max-w-[150px]" data-testid={`deal-demand-${d.deal_id}`}>
                      <div className="h-1 bg-[#14171A] border border-[#24272B] overflow-hidden">
                        <div className="h-full bg-[#E8E6E1]" style={{ width: `${demand.pct}%` }} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="min-w-[120px]">
              <span className={`t-cap ${cd.urgent ? "text-bone" : "text-muted-s"}`} data-testid={`deal-countdown-${d.deal_id}`}>{cd.text}</span>
            </div>
            <div className="t-label" data-testid={`deal-status-${d.deal_id}`}>
              {d.my_commitment ? "Committed" : d.status === "open" ? "Allocation open" : "Closed"}
            </div>
          </Link>
          );
        })}
        {deals.length === 0 && <p className="t-small text-muted-s hairline py-8">No deals available.</p>}
        {deals.length > 0 && shown.length === 0 && <p className="t-small text-muted-s hairline py-8" data-testid="deals-filter-empty">No deals match this filter.</p>}
      </div>

      {commitments.length > 0 && (
        <div className="mt-16">
          <span className="t-label">Your commitments</span>
          <table className="table-solera mt-4" data-testid="commitments-table">
            <thead>
              <tr><th>Company</th><th className="num">Committed</th><th className="num">Allocated</th><th>Status</th><th>Received</th></tr>
            </thead>
            <tbody>
              {commitments.map((c) => (
                <tr key={c.commitment_id}>
                  <td className="text-bone">{c.company}</td>
                  <td className="num text-bone">{fmtUSD(c.amount_usd)}</td>
                  <td className="num text-bone">{c.allocated_amount != null ? fmtUSD(c.allocated_amount) : "-"}</td>
                  <td className="text-muted-s capitalize">{c.status.replace("_", " ")}</td>
                  <td className="text-muted-s t-small fig">{new Date(c.created_at).toISOString().slice(0, 16).replace("T", " ")} UTC</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
